<template>
  <div class="contact-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">{{ t('menu.contact') }}</h1>
        <div class="header-divider"></div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="page-content">
      <div class="container">
        <!-- 调试：临时显示数据（可删除） -->
        <!-- <div style="text-align: center; margin-bottom: 20px; color: #999;">
          数据长度：{{ contactData.length }} | 数据内容：{{ JSON.stringify(contactData) }}
        </div> -->

        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ t('common.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="errorMessage" class="error-container">
          <div class="error-icon">
            <el-icon size="60"><Warning /></el-icon>
          </div>
          <h3 class="error-title">{{ t('common.error') }}</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-button" @click="fetchPageData">
            {{ t('common.retry') }}
          </button>
        </div>

        <!-- 联系列表：简化判断条件，直接判断长度 -->
        <div v-else-if="contactData.length > 0" class="contact-list">
          <!-- 移除ref绑定，简化动画逻辑，改用纯CSS动画延迟 -->
          <div
            v-for="(item, index) in contactData"
            :key="index"
            class="contact-item"
            :style="`animation-delay: ${index * 0.1}s`"
          >
            <!-- 联系人/咨询类型标题 -->
            <div class="contact-header">
              <el-icon class="header-icon"><User /></el-icon>
              <h3 class="contact-name">{{ item.name }}</h3>
              <span v-if="item.position" class="contact-position">{{ item.position }}</span>
            </div>

            <!-- 联系信息列表 -->
            <div class="contact-info-list">
              <!-- 电话 -->
              <div class="contact-info-item">
                <el-icon class="info-icon"><Phone /></el-icon>
                <span class="info-text">{{ item.phone }}</span>
              </div>

              <!-- 传真 -->
              <div class="contact-info-item">
                <el-icon class="info-icon"><TakeawayBox /></el-icon>
                <span class="info-text">{{ item.fax }}</span>
              </div>

              <!-- 邮箱 -->
              <div class="contact-info-item">
                <el-icon class="info-icon"><Message /></el-icon>
                <span class="info-text">{{ item.email }}</span>
              </div>

              <!-- 地址 -->
              <div class="contact-info-item address-item">
                <el-icon class="info-icon"><Location /></el-icon>
                <span class="info-text">{{ item.address }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-else class="empty-state">
          <div class="empty-icon">
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M40 70C56.5685 70 70 56.5685 70 40C70 23.4315 56.5685 10 40 10C23.4315 10 10 23.4315 10 40C10 56.5685 23.4315 70 40 70Z"
                stroke="var(--el-color-primary-light-8)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M30 40L40 50L50 30"
                stroke="var(--el-color-primary-light-6)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M40 25V40"
                stroke="var(--el-color-primary-light-6)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3 class="empty-title">{{ t('common.no_data', '暂无数据') }}</h3>
          <p class="empty-description">{{ t('contact.no_contact', '暂无联系信息') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Warning, User, Phone, TakeawayBox, Message, Location } from '@element-plus/icons-vue'
import { getSinglePageContent } from "@/assets/api/website";

// 使用 i18n（添加默认值避免翻译缺失导致渲染问题）
const { locale, t } = useI18n()

// 响应式数据（移除多余的ref和observer，简化逻辑）
const contactData = ref([])
const isLoading = ref(false)
const errorMessage = ref('')

// 获取页面数据（保留原有逻辑，添加调试日志确认数据）
const fetchPageData = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const currentLocale = locale.value
    // 接口返回的是处理后的response.data（拦截器已处理code）
    const response = await getSinglePageContent({
      lang: currentLocale,
      path: '/contact1'
    })

    // 调试：打印数据确认（可删除）
    console.log('联系数据response.content：', response.content)
    console.log('联系数据list：', response.content?.list || [])

    // 直接赋值，不修改任何内容（核心：确保数据正确存入contactData）
    contactData.value = response.content?.list || []
  } catch (error) {
    console.error('获取联系数据失败:', error)
    errorMessage.value = t('common.load_failed', '加载失败')
  } finally {
    isLoading.value = false
  }
}

// 监听语言变化
watch(locale, () => {
  fetchPageData()
})

// 组件挂载时获取数据
onMounted(() => {
  fetchPageData()
})
</script>

<style scoped lang="scss">
// 导入全局样式变量
@import '@/assets/css/common';

.contact-page {
  min-height: 100vh;
  background: linear-gradient(180deg, $gray-10 0%, $white 100%);
}

// 页面头部样式
.page-header {
  padding: 200px 0 100px;
  /* 背景核心优化：居中 + 覆盖 + 固定（可选） + 轻微模糊 */
  background: url('@/assets/img/header.jpg') center center / cover no-repeat;
  /* 高斯模糊：使用伪元素实现（避免模糊影响内容），模糊值 2px（极轻微） */
  position: relative;
  border-bottom: 1px solid rgba($primary-color, 0.1);

  /* 伪元素实现背景模糊（不影响文字/内容） */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: inherit; /* 继承父元素背景图 */
    filter: blur(4px); /* 极轻微高斯模糊，可调整（1-3px 都很淡） */
    z-index: 1; /* 放在内容下方 */
    transform: scale(1.01); /* 消除模糊边缘白边 */
  }

  /* 确保内容层级高于模糊层 */
  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 140px 0 60px;
  }

  @media (max-width: 480px) {
    padding: 140px 0 60px;
  }
}

.page-title {
  font-family: $font-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  margin-bottom: 20px;
  letter-spacing: 2px;
  /* 浅白色文字阴影：极淡、低模糊、小偏移，仅增强文字层次感 */
  text-shadow: 0 1px 2px $primary-color;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 16px;
    /* 移动端阴影稍淡，避免占视觉焦点 */
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.15);
  }
}


.header-divider {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, $primary-color, $secondary-color);
  margin: 0 auto;
  border-radius: 2px;

  @media (max-width: 480px) {
    width: 60px;
    height: 3px;
  }
}

// 主要内容区域
.page-content {
  padding: 60px 0;

  @media (max-width: 768px) {
    padding: 40px 0;
  }

  @media (max-width: 480px) {
    padding: 30px 0;
  }
}

// 联系列表：确保容器可见，一行一条数据
.contact-list {
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 30px; // 行间距
  padding: 0 20px; // 防止内容贴边

  @media (max-width: 768px) {
    gap: 24px;
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    gap: 20px;
    padding: 0 12px;
  }
}

// 单个联系项：移除动画阻塞，改用纯CSS动画（确保元素默认可见）
.contact-item {
  background: $white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: $shadow-light;
  border: 1px solid rgba($primary-color, 0.1);
  // 纯CSS淡入动画（默认执行，避免Intersection Observer导致的不显示）
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
  transform: translateY(20px);

  @media (max-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 480px) {
    padding: 20px 16px;
  }
}

// 动画关键帧（确保动画生效）
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 联系项头部
.contact-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid $primary-light-8;

  @media (max-width: 480px) {
    flex-wrap: wrap; // 防止内容溢出
    margin-bottom: 20px;
  }
}

.header-icon {
  color: $primary-color;
  font-size: 1.25rem;
}

.contact-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: $primary-color-2;
  font-family: $font-serif;
  margin: 0; // 清除默认margin

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
}

.contact-position {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 600;
  background: linear-gradient(135deg, $primary-light-9, $primary-light-7);
  color: $primary-color-2;
  border: 1px solid rgba($primary-color, 0.1);
  margin-left: 8px;

  @media (max-width: 480px) {
    margin-left: 0;
    margin-top: 8px; // 换行显示
  }
}

// 联系信息列表
.contact-info-list {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 480px) {
    gap: 12px;
  }
}

// 单个联系信息项
.contact-info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 1rem;
  color: $text-color;
  line-height: 1.6;

  // 地址项换行处理
  &.address-item {
    .info-text {
      white-space: pre-line;
      word-break: break-all; // 防止长文本溢出
    }
  }

  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
}

.info-icon {
  color: $primary-color;
  font-size: 1rem;
  flex-shrink: 0; // 防止图标被压缩
  margin-top: 2px; // 文本对齐
}

.info-text {
  flex: 1; // 占满剩余空间
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba($primary-color, 0.1);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  font-size: 1rem;
  color: $text-light;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 错误状态
.error-container {
  text-align: center;
  padding: 60px 20px;
  max-width: 600px;
  margin: 0 auto;
}

.error-icon {
  margin-bottom: 20px;
  color: $secondary-color-2;
}

.error-title {
  font-size: 1.5rem;
  color: $secondary-color-2;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.error-message {
  font-size: 1rem;
  color: $text-light;
  margin-bottom: 20px;
  line-height: 1.5;
}

.retry-button {
  background: $primary-color;
  color: $white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $primary-dark-1;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
}

// 空状态
.empty-state {
  text-align: center;
  padding: 80px 20px;
  max-width: 600px;
  margin: 0 auto;
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.7;
}

.empty-title {
  font-size: 1.5rem;
  color: $text-color;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
}

.empty-description {
  font-size: 1rem;
  color: $text-light;
  line-height: 1.5;
}
</style>